import {
  Button,
  createTableColumn,
  Dropdown,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  Option,
  type TableColumnDefinition,
  Text,
  Toast,
  Toaster,
  ToastTitle,
  Toolbar,
  Tooltip,
  tokens,
  useId,
  useToastController,
} from "@fluentui/react-components"
import {
  AddRegular,
  ArrowSwapRegular,
  ArrowSyncRegular,
  DeleteRegular,
  EditRegular,
  MoreHorizontalRegular,
  PeopleListRegular,
  PlugDisconnectedRegular,
  SearchRegular,
  ServerRegular,
} from "@fluentui/react-icons"
import type { NodeStatus } from "@rema/shared/schemas/common"
import type { Node } from "@rema/shared/schemas/nodes"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useSession } from "../../auth/session"
import { ConfirmDialog } from "../../components/confirm-dialog"
import { DataGridPage } from "../../components/data-grid-page"
import { EmptyState } from "../../components/empty-state"
import { PageHeader } from "../../components/page-header"
import { isApiError } from "../../lib/api"
import { problemMessage } from "../../lib/problem"
import { nodesApi } from "./api"
import { AssignmentsDrawer } from "./assignments-drawer"
import { nodesI18n } from "./i18n"
import { NodeFormDrawer } from "./node-form-drawer"
import { NodeStatusBadge } from "./node-status-badge"
import { ReassignDrawer } from "./reassign-drawer"

// Clave de cache del listado de nodos (la comparte el DataGrid y se invalida tras
// cada mutación para refrescar usuarios asignados, peso y estado).
const NODES_KEY = ["nodes"] as const

// Valores de estado para el filtro (mismo orden que el badge de estado).
const NODE_STATUS_VALUES: NodeStatus[] = ["active", "draining", "down", "disabled"]

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  search: { minWidth: "240px" },
  statusFilter: { minWidth: "180px" },
})

// Nodos que pueden recibir asignaciones nuevas (no down/disabled).
const isReassignTarget = (node: Node): boolean =>
  node.status === "active" || node.status === "draining"

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : nodesI18n.none

// Pantalla /nodes (docs/05): DataGrid de nodos con estado/peso/usuarios y acciones
// de alta, edición, baja, reasignación manual y vista de asignaciones por nodo.
export const NodesPage = () => {
  const styles = useStyles()
  const { hasScope } = useSession()
  const canWrite = hasScope("nodes:write")
  const queryClient = useQueryClient()
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<NodeStatus | "all">("all")

  const toasterId = useId("nodes-toaster")
  const { dispatchToast } = useToastController(toasterId)
  const notify = (message: string) =>
    dispatchToast(
      <Toast>
        <ToastTitle>{message}</ToastTitle>
      </Toast>,
      { intent: "success" },
    )

  // Estado de los paneles/diálogos.
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Node | null>(null)
  const [reassignOpen, setReassignOpen] = useState(false)
  const [reassignNodeId, setReassignNodeId] = useState<string | null>(null)
  const [assignmentsNode, setAssignmentsNode] = useState<Node | null>(null)
  const [toDelete, setToDelete] = useState<Node | null>(null)
  const [toDrain, setToDrain] = useState<Node | null>(null)
  const [rebalanceOpen, setRebalanceOpen] = useState(false)

  // Nodos elegibles como destino de reasignación (primera página, activos/drenando).
  const targetsQuery = useQuery({
    queryKey: ["nodes", "reassign-targets"],
    queryFn: async () => {
      const page = await nodesApi.list({ limit: 100 }, null)
      return page.data.filter(isReassignTarget)
    },
    enabled: reassignOpen,
  })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: NODES_KEY })
  }

  const deleteMutation = useMutation({
    mutationFn: (node: Node) => nodesApi.remove(node.id),
    onSuccess: () => {
      notify(nodesI18n.toast.deleted)
      setToDelete(null)
      invalidate()
    },
  })
  const drainMutation = useMutation({
    mutationFn: (node: Node) => nodesApi.drain(node.id, true),
    onSuccess: () => {
      notify(nodesI18n.toast.drained)
      setToDrain(null)
      invalidate()
    },
  })
  const rebalanceMutation = useMutation({
    mutationFn: () => nodesApi.rebalance(false),
    onSuccess: (res) => {
      notify(`${nodesI18n.toast.rebalanced} (${res.total})`)
      setRebalanceOpen(false)
      invalidate()
    },
  })

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }
  const openEdit = (node: Node) => {
    setEditing(node)
    setFormOpen(true)
  }
  const openReassign = (nodeId: string | null) => {
    setReassignNodeId(nodeId)
    setReassignOpen(true)
  }

  const columns: TableColumnDefinition<Node>[] = [
    createTableColumn<Node>({
      columnId: "name",
      renderHeaderCell: () => nodesI18n.columns.name,
      renderCell: (node) => <Text weight="semibold">{node.name}</Text>,
    }),
    createTableColumn<Node>({
      columnId: "baseUrl",
      renderHeaderCell: () => nodesI18n.columns.baseUrl,
      renderCell: (node) => <Text font="monospace">{node.base_url}</Text>,
    }),
    createTableColumn<Node>({
      columnId: "region",
      renderHeaderCell: () => nodesI18n.columns.region,
      renderCell: (node) => node.region ?? nodesI18n.none,
    }),
    createTableColumn<Node>({
      columnId: "weight",
      renderHeaderCell: () => nodesI18n.columns.weight,
      renderCell: (node) => String(node.weight),
    }),
    createTableColumn<Node>({
      columnId: "status",
      renderHeaderCell: () => nodesI18n.columns.status,
      renderCell: (node) => <NodeStatusBadge status={node.status} />,
    }),
    createTableColumn<Node>({
      columnId: "users",
      renderHeaderCell: () => nodesI18n.columns.users,
      renderCell: (node) => String(node.user_count ?? 0),
    }),
    createTableColumn<Node>({
      columnId: "lastHeartbeat",
      renderHeaderCell: () => nodesI18n.columns.lastHeartbeat,
      renderCell: (node) => formatDate(node.last_heartbeat_at),
    }),
    createTableColumn<Node>({
      columnId: "actions",
      renderHeaderCell: () => "",
      renderCell: (node) => (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              icon={<MoreHorizontalRegular />}
              aria-label={nodesI18n.actions.edit}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<PeopleListRegular />} onClick={() => setAssignmentsNode(node)}>
                {nodesI18n.actions.viewAssignments}
              </MenuItem>
              {canWrite ? (
                <>
                  <MenuItem icon={<EditRegular />} onClick={() => openEdit(node)}>
                    {nodesI18n.actions.edit}
                  </MenuItem>
                  <MenuItem
                    icon={<ArrowSwapRegular />}
                    disabled={!isReassignTarget(node)}
                    onClick={() => openReassign(node.id)}
                  >
                    {nodesI18n.actions.reassign}
                  </MenuItem>
                  <MenuItem
                    icon={<PlugDisconnectedRegular />}
                    disabled={node.status !== "active" && node.status !== "draining"}
                    onClick={() => setToDrain(node)}
                  >
                    {nodesI18n.actions.drain}
                  </MenuItem>
                  <MenuItem
                    icon={<DeleteRegular />}
                    disabled={(node.user_count ?? 0) > 0}
                    onClick={() => setToDelete(node)}
                  >
                    {nodesI18n.actions.delete}
                  </MenuItem>
                </>
              ) : null}
            </MenuList>
          </MenuPopover>
        </Menu>
      ),
    }),
  ]

  const deleteError =
    deleteMutation.isError && isApiError(deleteMutation.error)
      ? problemMessage(deleteMutation.error.problem)
      : null

  const toolbar = (
    <Toolbar className={styles.toolbar} aria-label={nodesI18n.filter.searchLabel}>
      <Input
        className={styles.search}
        value={search}
        onChange={(_, data) => setSearch(data.value)}
        placeholder={nodesI18n.filter.searchPlaceholder}
        contentBefore={<SearchRegular />}
        aria-label={nodesI18n.filter.searchLabel}
      />
      <Dropdown
        className={styles.statusFilter}
        value={statusFilter === "all" ? nodesI18n.filter.all : nodesI18n.status[statusFilter]}
        selectedOptions={[statusFilter]}
        onOptionSelect={(_, data) =>
          setStatusFilter((data.optionValue as NodeStatus | "all") ?? "all")
        }
        aria-label={nodesI18n.filter.statusLabel}
      >
        <Option value="all">{nodesI18n.filter.all}</Option>
        {NODE_STATUS_VALUES.map((s) => (
          <Option key={s} value={s}>
            {nodesI18n.status[s]}
          </Option>
        ))}
      </Dropdown>
    </Toolbar>
  )

  return (
    <>
      <Toaster toasterId={toasterId} />
      <PageHeader
        title={nodesI18n.page.title}
        description={nodesI18n.page.description}
        actions={
          canWrite ? (
            <>
              <Tooltip content={nodesI18n.actions.reassign} relationship="label">
                <Button icon={<ArrowSwapRegular />} onClick={() => openReassign(null)}>
                  {nodesI18n.actions.reassign}
                </Button>
              </Tooltip>
              <Button icon={<ArrowSyncRegular />} onClick={() => setRebalanceOpen(true)}>
                {nodesI18n.actions.rebalance}
              </Button>
              <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>
                {nodesI18n.actions.create}
              </Button>
            </>
          ) : null
        }
      />

      <DataGridPage<Node>
        queryKey={[...NODES_KEY, search.trim(), statusFilter]}
        fetchPage={(cursor) =>
          nodesApi.list(
            {
              limit: 50,
              status: statusFilter === "all" ? undefined : statusFilter,
              q: search.trim() || undefined,
            },
            cursor,
          )
        }
        columns={columns}
        getRowId={(node) => node.id}
        toolbar={toolbar}
        emptyState={
          <EmptyState
            icon={<ServerRegular />}
            title={nodesI18n.empty.title}
            description={nodesI18n.empty.description}
            action={
              canWrite ? (
                <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>
                  {nodesI18n.actions.create}
                </Button>
              ) : undefined
            }
          />
        }
      />

      <NodeFormDrawer
        open={formOpen}
        node={editing}
        onClose={() => setFormOpen(false)}
        onSaved={(mode) => {
          setFormOpen(false)
          notify(mode === "created" ? nodesI18n.toast.created : nodesI18n.toast.updated)
          invalidate()
        }}
      />

      <ReassignDrawer
        open={reassignOpen}
        nodes={targetsQuery.data ?? []}
        defaultNodeId={reassignNodeId}
        onClose={() => setReassignOpen(false)}
        onReassigned={() => {
          setReassignOpen(false)
          notify(nodesI18n.toast.reassigned)
          invalidate()
        }}
      />

      <AssignmentsDrawer node={assignmentsNode} onClose={() => setAssignmentsNode(null)} />

      <ConfirmDialog
        open={toDelete !== null}
        title={nodesI18n.delete.title}
        message={deleteError ?? nodesI18n.delete.message}
        confirmLabel={nodesI18n.delete.confirm}
        danger
        confirming={deleteMutation.isPending}
        onConfirm={() => {
          if (toDelete) deleteMutation.mutate(toDelete)
        }}
        onCancel={() => setToDelete(null)}
      />

      <ConfirmDialog
        open={toDrain !== null}
        title={nodesI18n.drainDialog.title}
        message={nodesI18n.drainDialog.message}
        confirmLabel={nodesI18n.drainDialog.confirm}
        confirming={drainMutation.isPending}
        onConfirm={() => {
          if (toDrain) drainMutation.mutate(toDrain)
        }}
        onCancel={() => setToDrain(null)}
      />

      <ConfirmDialog
        open={rebalanceOpen}
        title={nodesI18n.rebalanceDialog.title}
        message={nodesI18n.rebalanceDialog.message}
        confirmLabel={nodesI18n.rebalanceDialog.confirm}
        confirming={rebalanceMutation.isPending}
        onConfirm={() => rebalanceMutation.mutate()}
        onCancel={() => setRebalanceOpen(false)}
      />
    </>
  )
}
